import { useEffect, useMemo } from 'react';
import { EVENT_PAGE, getLessonPageUrl } from 'constants/pages';
import { withEngagementSource } from 'constants/payloads/club/engagementBeacon';
import { CURRENCY_USD } from 'constants/currency';
import {
  useGetCourtPageUpcomingEventsLazyQuery,
  useGetCourtPageUpcomingLessonsLazyQuery,
} from 'types/generated/client';
import { getCurrentSessionWindow } from 'utils/shared/time/getEventSchedule';
import { getActivityImageUrl } from 'utils/shared/getActivityImageUrl';
import { convertUnitPriceToFormattedPrice } from 'utils/shared/money/convertUnitPriceToFormattedPrice';
import { ProgramSummary } from './types';

const formatPrice = (
  amount: number | null | undefined,
  currency: string | null | undefined,
): string => {
  if (!amount) return '';
  return convertUnitPriceToFormattedPrice(amount, currency || CURRENCY_USD).priceDisplay;
};

export const useUpcomingPrograms = ({ venueId }: { venueId?: string }) => {
  const [getLessons, { data: lessonsData, loading: isLessonsLoading, called: isLessonsCalled }] =
    useGetCourtPageUpcomingLessonsLazyQuery();
  const [getEvents, { data: eventsData, loading: isEventsLoading, called: isEventsCalled }] =
    useGetCourtPageUpcomingEventsLazyQuery();

  useEffect(() => {
    if (venueId) {
      Promise.all([
        getLessons({ variables: { venueId } }),
        getEvents({ variables: { venueId, now: new Date().toISOString() } }),
      ]);
    }
  }, [venueId]);

  const programs = useMemo<ProgramSummary[]>(() => {
    const lessons: ProgramSummary[] = (lessonsData?.lessons || []).map((lesson) => ({
      id: lesson.id,
      href: withEngagementSource(getLessonPageUrl(lesson.id), 'CLUB_PAGE'),
      entityType: 'LESSON' as const,
      activityType: lesson.type,
      sport: lesson.sport ?? null,
      title: lesson.title,
      startDateTime: lesson.startDateTime,
      endDateTime: lesson.endDateTime ?? null,
      priceDisplay: formatPrice(lesson.priceUnitAmount, lesson.currency),
      locationName: lesson.venue?.title ?? null,
      organizerName: lesson.ownerProfile?.fullName ?? null,
      imageUrl: getActivityImageUrl(lesson),
      participantCount: lesson.participants.length,
      participantLimit: lesson.participantLimit ?? null,
      participantAvatarPaths: lesson.participants.map(
        (participant) => participant.userProfile?.profileImagePath ?? null,
      ),
    }));

    const events: ProgramSummary[] = (eventsData?.events || []).map((event) => {
      // Mirror the socials/open-plays marketplace semantics: external (scraped)
      // events only know their capacity, so show "{limit} players"; internal
      // events show registrations against the limit with avatars.
      const isExternalEvent = !!event.externalUrl?.trim();
      const participantLimit = event.participantLimit ?? 0;
      const hasParticipantLimit = participantLimit > 0;
      const registrationCount = event.registrationsAggregate?.aggregate?.count ?? 0;

      return {
        id: event.id,
        href: withEngagementSource(`${EVENT_PAGE}/${event.slug ?? event.id}`, 'CLUB_PAGE'),
        entityType: 'EVENT' as const,
        activityType: event.type,
        isRfx: event.isRfx,
        sport: event.sport ?? null,
        title: event.title,
        ...getCurrentSessionWindow(event),
        priceDisplay: formatPrice(event.registrationPriceUnitAmount, event.currency),
        locationName: event.venue?.title ?? null,
        organizerName: event.hostUserProfile?.fullName || event.sourceOrganizerTitle || null,
        imageUrl: getActivityImageUrl(event),
        participantCount: isExternalEvent
          ? hasParticipantLimit
            ? participantLimit
            : null
          : registrationCount,
        participantLimit: !isExternalEvent && hasParticipantLimit ? participantLimit : null,
        participantAvatarPaths: isExternalEvent
          ? []
          : (event.registrations || []).map(
              (registration) => registration.userProfile?.profileImagePath ?? null,
            ),
      };
    });

    return [...lessons, ...events].sort(
      (a, b) => new Date(a.startDateTime).getTime() - new Date(b.startDateTime).getTime(),
    );
  }, [lessonsData, eventsData]);

  const isLoading =
    !isLessonsCalled || !isEventsCalled || isLessonsLoading || isEventsLoading;

  return { programs, isLoading };
};
